import { ChangeDetectionStrategy, Component, computed, inject, signal, TrackByFunction, OnInit } from '@angular/core';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { ReactiveFormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { MatUploadButtonComponent } from '../mat-upload-button.component';
import {
  DETAILS_SERVICE_TOKEN,
  DetailsService,
} from 'app/features/admin/modules/modules-entity/templates/details/details/details.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DocumentsMetaData } from 'app/api/models';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { ModulesStore } from 'app/features/admin/modules/modules.store';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { UtilityService } from '../../services/utility.service';
import { tap } from 'rxjs';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { PrivateDocumentService } from '../../services/private-document.service';
import { validateUploadFiles } from '../upload/upload-validation.util';

@Component({
  selector: 'formly-field-file',
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    MatUploadButtonComponent,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTooltip,
    DatePipe,
    FormlyDataCyDirective,
  ],
  template: `
    <div class="flex flex-col mb-4" [formlyDataCy]="field">
      <!-- Upload Section -->
      @if (!isFieldDisabled() && allDocuments()?.length === 0) {
        <mat-file-upload
          class="flex-1"
          [labelText]="props.label + ' ' + (props.required ? '*' : '')"
          [info]="props.info"
          [descriptionText]="props.description"
          [selectButtonText]="props.selectButtonText ? props.selectButtonText : 'Kies document'"
          [allowMultipleFiles]="props.mutipleFiles"
          [showUploadButton]="props.showUploadButton"
          [customSvgIcon]="'close'"
          [acceptedTypes]="props.acceptedTypes"
          (selectedFilesChanged)="onFileSelected($event)">
        </mat-file-upload>
      }

      @if (uploadError(); as error) {
        <div class="mt-2 text-sm text-[var(--dd-field-fg-error)]" role="alert" data-cy="upload-error">{{ error }}</div>
      }

      <!-- Privacy toggle -->
      @if (!isFieldDisabled() && allDocuments()?.length === 0) {
        <div class="mt-2 mb-4" [hidden]="!showPrivateOptions()">
          <mat-checkbox [checked]="isPublic" (change)="togglePrivate($event.checked)"> Publiek document </mat-checkbox>
        </div>
      }

      <!-- Single File View -->
      @if (allDocuments().length === 1) {
        <div class="fuse-card bg-light-tint my-3 p-6 border">
          <div class="flex items-center justify-between w-full">
            <div class="flex flex-col gap-0">
              <div class="flex items-center gap-2">
                <mat-icon>{{ isImage(allDocuments()[0]) ? 'image' : 'description' }}</mat-icon>
                <span class="text-lg font-medium">{{ allDocuments()[0]?.documentName }}</span>
                <div class="flex items-center gap-1 text-sm text-accent">
                  <mat-icon
                    class="icon-size-4"
                    [svgIcon]="
                      publicDocuments().includes(allDocuments()[0])
                        ? 'heroicons_mini:globe-alt'
                        : 'heroicons_mini:lock-closed'
                    ">
                  </mat-icon>
                  <span>{{ publicDocuments().includes(allDocuments()[0]) ? 'Publiek' : 'Privé' }}</span>
                </div>
              </div>
              <span class="text-sm text-accent ml-8">
                Toegevoegd op {{ allDocuments()[0]?.createdDateTime | date: 'longDate' }}
              </span>
            </div>
            <div class="flex items-center gap-1">
              @if (isImage(allDocuments()[0])) {
                <button
                  mat-icon-button
                  type="button"
                  matTooltip="Voorbeeld bekijken"
                  (click)="openImagePreview(allDocuments()[0])">
                  <mat-icon svgIcon="mat_outline:visibility"></mat-icon>
                </button>
              }
              @if (publicDocuments().includes(allDocuments()[0])) {
                <button mat-icon-button (click)="copyLink(allDocuments()[0])" type="button">
                  <mat-icon svgIcon="feather:clipboard"></mat-icon>
                </button>
              }
              @if (getDownloadUrl(allDocuments()[0])) {
                <a mat-icon-button [href]="getDownloadUrl(allDocuments()[0])" target="_blank" rel="noopener noreferrer">
                  <mat-icon svgIcon="feather:download"></mat-icon>
                </a>
              } @else {
                <button mat-icon-button (click)="downloadDocument(allDocuments()[0])" type="button">
                  <mat-icon svgIcon="feather:download"></mat-icon>
                </button>
              }
              <button
                mat-icon-button
                data-cy="upload-delete-button"
                [disabled]="isFieldDisabled()"
                (click)="deleteDocumentConfirmation(allDocuments()[0])"
                type="button">
                <mat-icon svgIcon="feather:trash-2"></mat-icon>
              </button>
            </div>
          </div>
        </div>
      }

      <!-- Document Lists -->
      @if (allDocuments().length > 1) {
        <div class="space-y-6">
          <!-- Private Documents -->
          @if (privateDocuments()?.length > 0) {
            <div class="mt-4">
              <h3 class="text-lg font-semibold mb-2 flex items-center gap-2">
                <mat-icon [svgIcon]="'heroicons_mini:lock-closed'" class="text-accent"></mat-icon>
                Privé Documenten
              </h3>
              <div class="space-y-2">
                @for (doc of privateDocuments(); track trackByDocument($index, doc)) {
                  <div class="flex items-center justify-between p-3 rounded hover:text-accent-50 group">
                    <div class="flex flex-col">
                      <div class="flex items-center gap-2">
                        <mat-icon>{{ isImage(doc) ? 'image' : 'description' }}</mat-icon>
                        <span class="text-sm">{{ doc.documentName }}</span>
                      </div>
                      <span class="text-xs text-accent ml-6">
                        Toegevoegd op {{ doc.createdDateTime | date: 'longDate' }}
                      </span>
                    </div>
                    <div class="flex items-center gap-1">
                      @if (isImage(doc)) {
                        <button
                          mat-icon-button
                          type="button"
                          matTooltip="Voorbeeld bekijken"
                          (click)="openImagePreview(doc)">
                          <mat-icon svgIcon="mat_outline:visibility"></mat-icon>
                        </button>
                      }
                      @if (getDownloadUrl(doc)) {
                        <a mat-icon-button [href]="getDownloadUrl(doc)" target="_blank" rel="noopener noreferrer">
                          <mat-icon svgIcon="feather:download"></mat-icon>
                        </a>
                      } @else {
                        <button mat-icon-button (click)="downloadDocument(doc)" type="button">
                          <mat-icon svgIcon="feather:download"></mat-icon>
                        </button>
                      }
                      <button
                        mat-icon-button
                        data-cy="upload-delete-button"
                        [disabled]="isFieldDisabled()"
                        (click)="deleteDocumentConfirmation(doc)"
                        type="button">
                        <mat-icon svgIcon="feather:trash-2"></mat-icon>
                      </button>
                    </div>
                  </div>
                }
              </div>
            </div>
          }

          <!-- Public Documents -->
          @if (publicDocuments()?.length > 0) {
            <div class="mt-4">
              <h3 class="text-lg font-semibold mb-2 flex items-center gap-2">
                <mat-icon [svgIcon]="'heroicons_mini:globe-alt'"></mat-icon>
                Publieke Documenten
              </h3>
              <div class="space-y-2">
                @for (doc of publicDocuments(); track trackByDocument($index, doc)) {
                  <div class="flex items-center justify-between p-3 rounded hover:text-accent-50 group">
                    <div class="flex flex-col">
                      <div class="flex items-center gap-2">
                        <mat-icon>{{ isImage(doc) ? 'image' : 'description' }}</mat-icon>
                        <span class="text-sm">{{ doc.documentName }}</span>
                      </div>
                      <span class="text-xs text-accent ml-6">
                        Toegevoegd op {{ doc.createdDateTime | date: 'longDate' }}
                      </span>
                    </div>
                    <div class="flex items-center gap-1">
                      @if (isImage(doc)) {
                        <button
                          mat-icon-button
                          type="button"
                          matTooltip="Voorbeeld bekijken"
                          (click)="openImagePreview(doc)">
                          <mat-icon svgIcon="mat_outline:visibility"></mat-icon>
                        </button>
                      }
                      <button mat-icon-button (click)="copyLink(doc)" type="button">
                        <mat-icon svgIcon="feather:clipboard"></mat-icon>
                      </button>
                      <button mat-icon-button (click)="downloadDocument(doc)" type="button">
                        <mat-icon svgIcon="feather:download"></mat-icon>
                      </button>
                      <button
                        mat-icon-button
                        data-cy="upload-delete-button"
                        [disabled]="isFieldDisabled()"
                        (click)="deleteDocumentConfirmation(doc)"
                        type="button">
                        <mat-icon svgIcon="feather:trash-2"></mat-icon>
                      </button>
                    </div>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyFieldUploadComponent extends FieldType implements OnInit {
  detailsService = inject(DETAILS_SERVICE_TOKEN) as DetailsService;
  entriesService = inject(EntriesService);
  utilityService = inject(UtilityService);
  modulesStore = inject(ModulesStore);
  fuseConfirmationService = inject(FuseConfirmationService);
  private privateDocument = inject(PrivateDocumentService);
  private readonly imageExtensions = new Set(['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg']);
  isPublic = false;
  files: FileList;
  showPrivateOptions = computed(() => {
    return this.field.props.uploadType === 'public-and-private';
  });

  privateDocuments = computed(() => {
    const entry = this.detailsService.entry();
    return entry?.privateDocuments || [];
  });

  publicDocuments = computed(() => {
    const entry = this.detailsService.entry();
    return entry?.publicDocuments || [];
  });
  allDocuments = computed(() => {
    return [...this.privateDocuments(), ...this.publicDocuments()];
  });

  loadedFile = signal<string>(null);
  /** Rejection message from validateUploadFiles; cleared as soon as a valid file is picked. */
  readonly uploadError = signal<string | null>(null);

  // TrackBy function for better performance
  trackByDocument: TrackByFunction<DocumentsMetaData> = (index: number, doc: DocumentsMetaData) => {
    return doc.documentId || doc.documentName || index;
  };

  ngOnInit() {
    this.initializeUploadType();
  }

  /**
   * Get the download URL for a document.
   * Only returns URL for public documents (blobUrl).
   * Private documents return null and use the download button which opens via browser navigation,
   * bypassing CORS restrictions that occur when following cross-origin redirects.
   *
   * Note: The API returns a 302 redirect to Azure Blob Storage for private documents.
   * When JavaScript follows cross-origin redirects, browsers set Origin to null for security,
   * causing Azure CORS to reject the request.
   */
  getDownloadUrl(doc: DocumentsMetaData): string | null {
    if (this.publicDocuments().includes(doc)) {
      return doc.blobUrl || null;
    }
    // Private documents use download button (browser navigation) to bypass CORS
    return null;
  }

  isImage(doc: DocumentsMetaData): boolean {
    const name = doc?.documentName || doc?.blobReference;
    if (!name) return false;
    const lastDot = name.lastIndexOf('.');
    if (lastDot === -1) return false;
    return this.imageExtensions.has(name.substring(lastDot).toLowerCase());
  }

  openImagePreview(doc: DocumentsMetaData): void {
    if (!this.isImage(doc)) return;

    // Public: directly open the blob URL in a new tab (browser sends cookies).
    if (doc.blobUrl) {
      window.open(doc.blobUrl, '_blank', 'noopener');
      return;
    }

    // Private: the backend returns the file URL as JSON; PrivateDocumentService
    // fetches the image itself with a correct Origin and opens it inline.
    const entity = this.detailsService.entity();
    const entry = this.detailsService.entry();
    if (!entity || !entry || !doc.documentId) return;

    this.privateDocument.openImagePreview(entity.id, entry.id, doc.documentId).subscribe({
      error: error => {
        console.error('Failed to open image preview:', error);
        this.utilityService.notify('Kon voorbeeld niet openen');
      },
    });
  }

  private initializeUploadType(): void {
    if (this.field.props.uploadType === 'public') {
      this.isPublic = true;
    } else if (this.field.props.uploadType === 'private') {
      this.isPublic = false;
    }
  }

  isFieldDisabled(): boolean {
    return this.form.disabled || this.props.disabled === true;
  }

  onFileSelected(files: FileList) {
    if (this.isFieldDisabled()) return;
    this.field.formControl.markAsTouched();
    if (!files) return;

    // The picker's accept attribute filters the dialog and enforces nothing, so the configured
    // types and the size ceiling are checked here — the same shared rules the other upload fields
    // apply. Without this the settings would be visible in the form builder but toothless.
    const validation = validateUploadFiles(
      files,
      this.props?.['acceptedTypes'] as string[] | string | undefined,
      this.props?.['maxFileSizeMb'] as number | undefined
    );
    if (validation.error) {
      this.uploadError.set(validation.error);
      return;
    }
    this.uploadError.set(null);

    this.files = files;
    if (files.length > 0) {
      this.detailsService.addPendingFiles$.next({
        fieldKey: this.field.props.keyPath as string,
        files: this.files,
        isPublic: this.isPublic,
      });

      const file = files[0];
      const reader = new FileReader();
      reader.onload = e => {
        if (typeof e.target?.result === 'string') {
          this.loadedFile.set(e.target?.result);
          this.formControl.setValue('uploading');
          // A picked file is a user change; without this the save button's dirty gate
          // never enables for an upload-only edit.
          this.formControl.markAsDirty();
        }
      };
      reader.readAsDataURL(file);
    }
  }

  togglePrivate(checked: boolean) {
    if (this.isFieldDisabled()) return;
    this.isPublic = checked;
    if (this.formControl.value) {
      this.detailsService.addPendingFiles$.next({
        fieldKey: this.field.props.keyPath as string,
        files: this.files,
        isPublic: this.isPublic,
      });
    }
  }

  downloadDocument(doc: DocumentsMetaData) {
    const entity = this.detailsService.entity();
    const entry = this.detailsService.entry();

    if (!entity || !entry) return;

    this.modulesStore.downloadDocument({
      moduleId: entity.id,
      entryId: entry.id,
      documentId: doc.documentId,
      // Prefer the entry's name (the document's title); the metadata
      // documentName is often empty, which falls back to the raw blob file
      // name (a GUID). The service appends the real extension.
      documentName: (entry.entry?.['name'] as string) || doc.documentName,
      blobUrl: doc.blobUrl,
    });
  }

  deleteDocumentConfirmation(doc: DocumentsMetaData) {
    if (this.isFieldDisabled()) return;
    const entry = this.detailsService.entry();
    if (!entry) return;

    const confirmation = this.fuseConfirmationService.open({
      title: 'Document verwijderen',
      message: `Weet je zeker dat je '${doc.documentName}' wil verwijderen?`,
      actions: {
        cancel: { label: 'Annuleren' },
        confirm: { label: 'Verwijderen' },
      },
    });

    confirmation.afterClosed().subscribe(result => {
      if (result === 'confirmed') {
        const entity = this.detailsService.entity();
        if (!entity) return;

        this.deleteDocument(doc);
      }
    });
  }

  deleteDocument(doc: DocumentsMetaData) {
    if (this.isFieldDisabled()) return;
    const isPublic = this.publicDocuments().some(d => d.documentId === doc.documentId);

    this.modulesStore.deleteDocument({
      moduleId: this.detailsService.entity().id,
      entryId: this.detailsService.entry().id,
      documentId: doc.documentId,
      isPublic: isPublic,
    });

    if (this.detailsService.entryModal()?.id === this.detailsService.entry().id) {
      // TODO improve this by using a 'temp entry state' for 'linked items' component
      setTimeout(() => {
        // get the latest version of entry because etag is changed and there is no state of the entry
        this.entriesService
          .getEntry(this.detailsService.entity().id, this.detailsService.entry().id)
          .pipe(
            tap(entry => {
              this.detailsService.setEntryModal$.next({
                moduleId: this.detailsService.entity().id,
                entryId: this.detailsService.entry().id,
                entry: entry,
              });
            })
          )
          .subscribe();
      }, 2000);
    }
  }

  copyLink(doc: DocumentsMetaData) {
    if (doc.blobUrl) {
      navigator.clipboard.writeText(doc.blobUrl);
      this.utilityService.notify('Link gekopieerd naar klembord');
    }
  }
}
